import {
  Color3, CreateBox, CreateCylinder, CreateIcoSphere, CreatePlane, CreateSphere, CreateTorus,
  DynamicTexture, Mesh, PBRMaterial, TransformNode, Vector3, VertexData,
  type AbstractMesh, type Scene,
} from "@babylonjs/core";
import {
  LANTERN_INTERACTIONS, LANTERN_ORIGIN, LANTERN_PATHS, LANTERN_REWARDS,
  LANTERN_SITES, LANTERN_SOLIDS, terrainHeight, type LanternSiteId,
} from "@tiny-seas/shared";
import { Materials, SURFACE_PROFILES, boxProjectUVs, sphereProjectUVs, surfaceForSlot, type PaletteKey } from "./materials.ts";

export interface LanternVisualState {
  bearing: number;
  colour: number;
  revealed: boolean;
  cargoTaken: boolean;
  cacheTaken: boolean;
  supplyCooldown: number;
}

/** Collision comes exclusively from shared authoring. Detail never fills an opening. */
export class LanternScene {
  private readonly batches = new Map<string, { meshes: Mesh[]; shadows: boolean }>();
  private readonly lensRoot: TransformNode;
  private readonly lensMaterial: PBRMaterial;
  private readonly beamMaterial: PBRMaterial;
  private readonly cargoMarker: TransformNode;
  private readonly cargoWrapping: Mesh;
  private readonly cacheLid: Mesh;
  private readonly supplyGlow: Mesh;
  private readonly cacheX: number;
  private bearing = -1;
  private colour = -1;
  private revealed = false;
  private cargoTaken = false;
  private cacheTaken = false;
  private suppliesReady = true;

  constructor(
    private readonly scene: Scene,
    private readonly mats: Materials,
    addCaster: (mesh: AbstractMesh) => void,
    private readonly surfaceHeight: (x: number, z: number) => number,
  ) {
    this.structures();
    this.paths();
    this.landing();
    this.cottage();
    this.tower();
    this.wreck();
    this.overlook();
    this.grotto();
    this.storyObjects();
    this.coastalClusters();

    const tower = this.site("lighthouse");
    this.lensRoot = new TransformNode("lantern:lens-direction", scene);
    this.lensRoot.position.set(tower.x, 26.65, tower.z);
    this.lensMaterial = this.mats.get("lamp", { emissive: 3 }).clone("lantern:signal-lens");
    this.lensMaterial.unlit = true;
    const lens = CreateSphere("lantern:fresnel-lens", { diameter: 1.5, segments: 16 }, scene);
    lens.scaling.set(0.45, 1, 1); lens.position.x = -0.6;
    this.dynamic(lens, this.lensMaterial, this.lensRoot);
    for (let i = 0; i < 7; i++) {
      const ring = CreateTorus("lantern:lens-prism", { diameter: 1.55 - Math.abs(i - 3) * 0.14, thickness: 0.055, tessellation: 24 }, scene);
      ring.rotation.z = Math.PI / 2; ring.position.x = -0.5 - i * 0.045;
      this.dynamic(ring, this.mats.get("brass"), this.lensRoot);
    }
    const back = CreateCylinder("lantern:lens-housing", { diameter: 1.7, height: 0.18, tessellation: 16 }, scene);
    back.rotation.z = Math.PI / 2; back.position.x = 0.05;
    this.dynamic(back, this.mats.get("iron"), this.lensRoot);
    this.beamMaterial = this.mats.color("lantern:signal-beam", "#ffd36b", 1.4, 0.07);
    this.beamMaterial.unlit = true;
    this.beamMaterial.backFaceCulling = false;
    this.beamMaterial.disableDepthWrite = true;
    const beam = CreateCylinder("lantern:directional-beacon", { diameterTop: 14, diameterBottom: 0.8, height: 100, tessellation: 24 }, scene);
    beam.rotation.z = Math.PI / 2; beam.position.x = -50.8;
    this.dynamic(beam, this.beamMaterial, this.lensRoot);

    const cargo = LANTERN_REWARDS.cargo;
    this.cargoMarker = new TransformNode("lantern:revealed-cargo-marker", scene);
    this.cargoMarker.position.set(cargo.x, cargo.y, cargo.z);
    const pennantPole = CreateCylinder("lantern:salvage-marker-pole", { diameter: 0.06, height: 2.2, tessellation: 6 }, scene);
    pennantPole.position.set(-0.7, 1.1, 0);
    this.dynamic(pennantPole, this.mats.get("wood"), this.cargoMarker);
    const pennant = CreatePlane("lantern:salvage-pennant", { width: 0.65, height: 0.38, sideOrientation: Mesh.DOUBLESIDE }, scene);
    pennant.position.set(-0.36, 1.88, 0);
    this.dynamic(pennant, this.mats.get("lamp", { emissive: 0.6 }), this.cargoMarker);
    this.cargoWrapping = CreateBox("lantern:salvage-oilcloth", { width: 1.1, height: 0.28, depth: 0.8 }, scene);
    this.cargoWrapping.position.set(cargo.x, cargo.y + 0.14, cargo.z);
    boxProjectUVs(this.cargoWrapping, SURFACE_PROFILES.fabric.metres);
    this.dynamic(this.cargoWrapping, this.mats.get("cloth"));
    const cache = this.interaction("cache");
    this.cacheX = cache.x;
    this.cacheLid = CreateBox("lantern:cache-lid", { width: 1.3, height: 0.08, depth: 1 }, scene);
    this.cacheLid.position.set(cache.x, cache.y - 0.56, cache.z);
    boxProjectUVs(this.cacheLid, SURFACE_PROFILES.wood.metres);
    this.dynamic(this.cacheLid, this.mats.get("woodDark"));
    const supplies = this.interaction("supplies");
    this.supplyGlow = CreateSphere("lantern:supply-ready-lamp", { diameter: 0.13, segments: 8 }, scene);
    this.supplyGlow.position.set(supplies.x + 0.46, supplies.y + 0.02, supplies.z);
    this.dynamic(this.supplyGlow, this.mats.get("lamp", { emissive: 1.5 }));

    for (const [key, batch] of this.batches) {
      const merged = batch.meshes.length === 1 ? batch.meshes[0]! : Mesh.MergeMeshes(batch.meshes, true, true);
      if (!merged) throw new Error(`Cannot merge Lantern site ${key}`);
      merged.name = `lantern:site:${key}`;
      merged.isPickable = false;
      merged.receiveShadows = batch.shadows;
      merged.freezeWorldMatrix();
      if (batch.shadows) addCaster(merged);
    }
    this.batches.clear();
    this.cargoMarker.setEnabled(false);
    this.cargoWrapping.setEnabled(false);
    this.setState({ bearing: 2, colour: 1, revealed: false, cargoTaken: false, cacheTaken: false, supplyCooldown: 0 });
  }

  /** State comparisons keep the render loop allocation-free; signals do not depend on motion. */
  setState(state: LanternVisualState): void {
    if (state.bearing !== this.bearing || state.colour !== this.colour || state.revealed !== this.revealed) {
      this.bearing = state.bearing;
      if (state.revealed && state.bearing === 0 && state.colour === 0) {
        const cargo = LANTERN_REWARDS.cargo;
        const dx = cargo.x - this.lensRoot.position.x, dz = cargo.z - this.lensRoot.position.z;
        this.lensRoot.rotation.y = Math.atan2(dz, -dx);
        this.lensRoot.rotation.z = Math.atan2(this.lensRoot.position.y - cargo.y - 1.2, Math.hypot(dx, dz));
      } else {
        this.lensRoot.rotation.y = -state.bearing * Math.PI / 2;
        this.lensRoot.rotation.z = 0;
      }
    }
    if (state.colour !== this.colour) {
      this.colour = state.colour;
      if (state.colour === 0) this.lensMaterial.albedoColor.set(1, 0.57, 0.1);
      else if (state.colour === 2) this.lensMaterial.albedoColor.set(1, 0.035, 0.02);
      else this.lensMaterial.albedoColor.set(0.85, 0.94, 1);
      this.lensMaterial.emissiveColor.copyFrom(this.lensMaterial.albedoColor).scaleInPlace(3);
      this.beamMaterial.albedoColor.copyFrom(this.lensMaterial.albedoColor);
      this.beamMaterial.emissiveColor.copyFrom(this.lensMaterial.albedoColor).scaleInPlace(1.4);
    }
    if (state.revealed !== this.revealed || state.cargoTaken !== this.cargoTaken) {
      this.revealed = state.revealed; this.cargoTaken = state.cargoTaken;
      this.cargoMarker.setEnabled(state.revealed && !state.cargoTaken);
      this.cargoWrapping.setEnabled(state.revealed && !state.cargoTaken);
    }
    if (state.cacheTaken !== this.cacheTaken) {
      this.cacheTaken = state.cacheTaken;
      this.cacheLid.rotation.z = state.cacheTaken ? -0.8 : 0;
      this.cacheLid.position.x = this.cacheX + (state.cacheTaken ? 0.95 : 0);
    }
    const ready = state.supplyCooldown <= 0;
    if (ready !== this.suppliesReady) {
      this.suppliesReady = ready;
      this.supplyGlow.material = this.mats.get(ready ? "lamp" : "iron", ready ? { emissive: 1.5 } : {});
    }
  }

  private site(id: LanternSiteId) { return LANTERN_SITES.find(site => site.id === id)!; }
  private interaction(id: (typeof LANTERN_INTERACTIONS)[number]["id"]) { return LANTERN_INTERACTIONS.find(item => item.id === id)!; }
  private world(x: number, z: number): [number, number] { return [LANTERN_ORIGIN.x + x, LANTERN_ORIGIN.z + z]; }

  private add(mesh: Mesh, site: string, key: PaletteKey, shadows = true): Mesh {
    mesh.material = this.mats.get(key, key === "lamp" ? { emissive: 1.8 } : {});
    mesh.isPickable = false;
    const id = `${site}:${key}:${shadows}`;
    let batch = this.batches.get(id);
    if (!batch) this.batches.set(id, batch = { meshes: [], shadows });
    batch.meshes.push(mesh);
    return mesh;
  }
  private dynamic(mesh: Mesh, material: PBRMaterial, root?: TransformNode): void {
    mesh.material = material; mesh.isPickable = false; mesh.parent = root ?? null;
  }
  private box(site: string, key: PaletteKey, x: number, y: number, z: number, w: number, h: number, d: number, yaw = 0): Mesh {
    const mesh = CreateBox(`lantern:${site}:${key}`, { width: w, height: h, depth: d }, this.scene);
    mesh.position.set(x, y, z); mesh.rotation.y = yaw;
    boxProjectUVs(mesh, SURFACE_PROFILES[surfaceForSlot(key)].metres);
    return this.add(mesh, site, key);
  }
  private cylinder(site: string, key: PaletteKey, x: number, y: number, z: number, r: number, h: number, top = r, sides = 12): Mesh {
    const mesh = CreateCylinder(`lantern:${site}:${key}`, { diameter: r * 2, diameterTop: top * 2, height: h, tessellation: sides }, this.scene);
    mesh.position.set(x, y, z);
    const uv = mesh.getVerticesData("uv")!, positions = mesh.getVerticesData("position")!, normals = mesh.getVerticesData("normal")!;
    const repeat = SURFACE_PROFILES[surfaceForSlot(key)].metres;
    for (let i = 0; i < positions.length / 3; i++) {
      if (Math.abs(normals[i * 3 + 1]!) > 0.9) {
        uv[i * 2] = positions[i * 3]! / repeat; uv[i * 2 + 1] = positions[i * 3 + 2]! / repeat;
      } else { uv[i * 2] = uv[i * 2]! * Math.PI * 2 * r / repeat; uv[i * 2 + 1] = uv[i * 2 + 1]! * h / repeat; }
    }
    mesh.setVerticesData("uv", uv, false);
    return this.add(mesh, site, key, key !== "lamp");
  }
  private rock(site: string, x: number, y: number, z: number, w: number, h: number, d: number, dark = false): Mesh {
    const size = Math.max(w, h, d);
    const mesh = CreateIcoSphere(`lantern:${site}:weathered-rock`, { radius: 0.5, subdivisions: size > 2 ? 2 : 1, flat: true }, this.scene);
    mesh.scaling.set(w, h, d);
    mesh.bakeCurrentTransformIntoVertices();
    sphereProjectUVs(mesh, SURFACE_PROFILES.rock.metres);
    mesh.position.set(x, y, z); mesh.rotation.y = x * 1.7 + z;
    // Sub-metre trail pebbles need neither dense spheres nor three shadow-cascade passes.
    return this.add(mesh, site, dark ? "rockDark" : "rock", size > 1);
  }
  private beam(site: string, key: PaletteKey, a: Vector3, b: Vector3, thickness: number): Mesh {
    const mid = a.add(b).scaleInPlace(0.5), delta = b.subtract(a);
    const mesh = this.box(site, key, mid.x, mid.y, mid.z, thickness, delta.length(), thickness);
    mesh.rotation.z = -Math.atan2(delta.x, delta.y);
    mesh.rotation.x = Math.atan2(delta.z, Math.hypot(delta.x, delta.y));
    return mesh;
  }

  private structures(): void {
    for (const solid of LANTERN_SOLIDS) {
      const c = solid.collider;
      if (c.kind === "cyl") {
        this.cylinder(solid.site, solid.material, c.x, (c.y0 + c.y1) / 2, c.z, c.r, c.y1 - c.y0);
      } else if (solid.id.includes("rail") || solid.id.includes("guard")) {
        const x = (c.minX + c.maxX) / 2, z = (c.minZ + c.maxZ) / 2;
        const w = c.maxX - c.minX, d = c.maxZ - c.minZ;
        this.box(solid.site, solid.material, x, c.y1 - 0.045, z, w, 0.09, d);
        this.box(solid.site, solid.material, x, c.y0 + 0.12, z, w, 0.07, d);
        const alongX = w > d, length = Math.max(w, d), count = Math.ceil(length / 1.1);
        for (let i = 0; i <= count; i++) {
          const offset = length * (i / count - 0.5);
          this.box(solid.site, solid.material, x + (alongX ? offset : 0), (c.y0 + c.y1) / 2, z + (alongX ? 0 : offset), 0.07, c.y1 - c.y0, 0.07);
        }
      } else if (solid.id === "journal-table" || solid.id === "logbook-desk") {
        this.box(solid.site, "plank", (c.minX + c.maxX) / 2, c.y1 - 0.065, (c.minZ + c.maxZ) / 2, c.maxX - c.minX, 0.13, c.maxZ - c.minZ);
        for (const x of [c.minX + 0.06, c.maxX - 0.06]) for (const z of [c.minZ + 0.06, c.maxZ - 0.06])
          this.box(solid.site, "woodDark", x, (c.y0 + c.y1 - 0.13) / 2, z, 0.12, c.y1 - c.y0 - 0.13, 0.12);
      } else if (solid.site === "lighthouse" && (solid.material === "plaster" || solid.material === "paintRed") && c.y1 - c.y0 > 2) {
        // Masonry bands split only colour, never the exact shared footprint.
        let bottom = c.y0;
        while (bottom < c.y1) {
          const band = Math.floor((bottom - 10 + 1e-6) / 2.8);
          const top = Math.min(c.y1, 10 + (band + 1) * 2.8);
          this.box(solid.site, band % 2 ? "paintRed" : "plaster", (c.minX + c.maxX) / 2, (bottom + top) / 2, (c.minZ + c.maxZ) / 2, c.maxX - c.minX, top - bottom, c.maxZ - c.minZ);
          bottom = top;
        }
      } else {
        this.box(solid.site, solid.material, (c.minX + c.maxX) / 2, (c.y0 + c.y1) / 2, (c.minZ + c.maxZ) / 2, c.maxX - c.minX, c.y1 - c.y0, c.maxZ - c.minZ);
      }
    }
  }

  private paths(): void {
    for (const path of LANTERN_PATHS) {
      for (let j = 1; j < path.points.length; j++) {
        const a = path.points[j - 1]!, b = path.points[j]!;
        const dx = b.x - a.x, dz = b.z - a.z, length = Math.hypot(dx, dz);
        if (length < 0.01) continue;
        const nx = -dz / length, nz = dx / length, count = Math.ceil(length / 0.7);
        const positions: number[] = [], indices: number[] = [], uvs: number[] = [], normals: number[] = [];
        for (let i = 0; i <= count; i++) for (const side of [-1, 1]) {
          const t = i / count, distance = t * length;
          const halfWidth = path.width * (0.45 + 0.035 * Math.sin(distance * 0.7 + path.id.length));
          const x = a.x + dx * t + nx * side * halfWidth, z = a.z + dz * t + nz * side * halfWidth;
          positions.push(x, this.surfaceHeight(x, z) + 0.035, z);
          uvs.push(side * path.width / SURFACE_PROFILES.ground.metres, t * length / SURFACE_PROFILES.ground.metres);
        }
        for (let i = 0; i < count; i++) { const n = i * 2; indices.push(n, n + 2, n + 1, n + 1, n + 2, n + 3); }
        VertexData.ComputeNormals(positions, indices, normals);
        const data = new VertexData(); data.positions = positions; data.indices = indices; data.uvs = uvs; data.normals = normals;
        const mesh = new Mesh(`lantern:path:${path.id}:${j}`, this.scene); data.applyToMesh(mesh);
        this.add(mesh, `path:${path.id}`, "dirt", false);
        for (let distance = 1.5; distance < length; distance += 2.2 + Math.sin(distance * 1.41) * 0.65) {
          const t = distance / length;
          for (const side of [-1, 1]) {
            const edge = path.width * (0.45 + 0.035 * Math.sin(distance * 0.7 + path.id.length)) + 0.15;
            const x = a.x + dx * t + nx * side * edge, z = a.z + dz * t + nz * side * edge;
            const size = 0.33 + (0.5 + Math.sin(distance * 2.3 + side) * 0.5) * 0.35;
            this.rock(`path-edge:${path.id}`, x, this.surfaceHeight(x, z) + 0.05, z, size, 0.16, size * 0.75);
          }
        }
      }
    }
  }

  private plaque(site: string, text: string, x: number, y: number, z: number, width = 1.5, yaw = 0): void {
    const board = this.box(site, "woodDark", x, y, z, width + 0.15, 0.49, 0.08, yaw);
    const face = CreatePlane(`lantern:${site}:inscription`, { width, height: 0.35 }, this.scene);
    face.position.set(x - Math.sin(yaw) * 0.047, y, z - Math.cos(yaw) * 0.047); face.rotation.y = yaw;
    const texture = new DynamicTexture(`lantern:${site}:plaque`, { width: 512, height: 128 }, this.scene, false);
    const fontSize = Math.min(44, Math.floor(460 / (text.length * 0.64)));
    texture.drawText(text, null, 80, `bold ${fontSize}px Georgia`, "#f3e6c4", "#523722", true);
    const mat = this.mats.get("woodDark").clone(`lantern:${site}:plaque-material`);
    mat.albedoTexture = texture; mat.bumpTexture = null; mat.metallicTexture = null; mat.albedoColor = Color3.White();
    face.material = mat; face.isPickable = false; face.freezeWorldMatrix();
    board.receiveShadows = true;
  }

  private lantern(site: string, x: number, y: number, z: number): void {
    this.cylinder(site, "iron", x, y - 0.18, z, 0.2, 0.08, 0.2, 8);
    this.cylinder(site, "lamp", x, y, z, 0.13, 0.3, 0.13, 8);
    this.cylinder(site, "iron", x, y + 0.2, z, 0.22, 0.12, 0.1, 8);
    for (const dx of [-0.13, 0.13]) for (const dz of [-0.13, 0.13]) this.box(site, "iron", x + dx, y, z + dz, 0.025, 0.4, 0.025);
  }

  private landing(): void {
    const [x, z] = this.world(39, -49);
    this.cylinder("landing", "wood", x, 2.6, z, 0.11, 2.1, 0.09, 8);
    this.plaque("landing", "LANTERN ISLE", x, 3.12, z, 2.4);
    this.lantern("landing", x, 3.55, z);
    for (let i = 0; i < 9; i++) {
      const [px, pz] = this.world(44 + Math.sin(i * 1.7) * 6, -44 + Math.cos(i * 1.7) * 5);
      const plank = this.box("landing", i % 2 ? "woodDark" : "plank", px, this.surfaceHeight(px, pz) + 0.07, pz, 0.24, 0.09, 1.2 + i % 3 * 0.45, i * 1.83);
      plank.rotation.z = 0.06;
    }
    const [cx, cz] = this.world(43, -43);
    this.box("landing", "plank", cx, terrainHeight(cx, cz) + 0.32, cz, 0.8, 0.6, 0.7, 0.2);
    this.box("landing", "iron", cx, terrainHeight(cx, cz) + 0.34, cz, 0.82, 0.06, 0.72, 0.2);
    this.cylinder("landing", "rope", cx - 1.1, terrainHeight(cx - 1.1, cz) + 0.08, cz, 0.45, 0.1, 0.45, 16);
  }

  private cottage(): void {
    const { x, z } = this.site("cottage");
    // A pitched tiled canopy rests on the authored collision roof; doors stay open.
    for (const side of [-1, 1]) {
      const roof = this.box("cottage", "roof", x + side * 2.2, 7.22, z, 4.65, 0.15, 9.1);
      roof.rotation.z = side * -0.35;
      this.box("cottage", "woodDark", x + side * 4.45, 6.46, z, 0.16, 0.22, 9.25);
    }
    for (const side of [-1, 1]) {
      const positions = [x - 4.35, 6.5, z + side * 4.35, x + 4.35, 6.5, z + side * 4.35, x, 8.03, z + side * 4.35];
      const indices = side > 0 ? [0, 2, 1] : [0, 1, 2], normals: number[] = [];
      VertexData.ComputeNormals(positions, indices, normals);
      const data = new VertexData(); data.positions = positions; data.indices = indices; data.normals = normals;
      data.uvs = [0, 0, 8.7, 0, 4.35, 1.53];
      const gable = new Mesh("lantern:cottage:plaster-gable", this.scene); data.applyToMesh(gable);
      this.add(gable, "cottage", "plaster");
      this.beam("cottage", "woodDark", new Vector3(x - 4.35, 6.5, z + side * 4.38), new Vector3(x, 8.03, z + side * 4.38), 0.09);
      this.beam("cottage", "woodDark", new Vector3(x, 8.03, z + side * 4.38), new Vector3(x + 4.35, 6.5, z + side * 4.38), 0.09);
    }
    this.box("cottage", "roof", x, 8.02, z, 0.34, 0.2, 9.2);
    this.box("cottage", "rockDark", x + 2.5, 7.7, z + 2.1, 0.8, 3.2, 0.85);
    this.box("cottage", "rock", x + 2.5, 9.32, z + 2.1, 1, 0.18, 1.05);
    for (const dx of [-3.9, 3.9]) for (const dz of [-3.9, 3.9]) this.box("cottage", "woodDark", x + dx, 4.6, z + dz, 0.12, 3.1, 0.12);
    this.plaque("cottage", "KEEPER'S HOUSE", x, 6.04, z - 4.18, 2.5);
    this.lantern("cottage", x - 1.55, 5.2, z - 4.21);
    for (const y of [4.05, 5.35]) this.box("cottage", "woodLight", x + 4.08, y, z + 0.5, 0.13, 0.12, 3.2);
    for (const dz of [-1.08, 2.08]) this.box("cottage", "woodLight", x + 4.08, 4.7, z + dz, 0.13, 1.4, 0.1);
    for (const dz of [-1.55, 2.55]) this.box("cottage", "cloth", x + 4.13, 4.7, z + dz, 0.08, 1.2, 0.7);
    // Compact furnishings sit against walls, outside the doorway/journal approach.
    this.box("cottage", "sail", x - 2.55, 3.65, z + 1.6, 1.45, 0.2, 2.65);
    this.box("cottage", "cloth", x - 2.55, 3.78, z + 2.05, 1.48, 0.06, 1.6);
    this.box("cottage", "paper", x - 2.55, 3.82, z + 0.62, 1.05, 0.15, 0.38);
    this.box("cottage", "woodDark", x + 3.3, 4.15, z + 2.65, 0.75, 2.2, 1.6);
    for (let i = 0; i < 3; i++) {
      this.box("cottage", "woodLight", x + 3.28, 3.7 + i * 0.65, z + 2.65, 0.8, 0.08, 1.65);
      this.cylinder("cottage", "brass", x + 3.24, 3.91 + i * 0.65, z + 2.8, 0.13, 0.33);
    }
    this.box("cottage", "cloth", x, 3.018, z + 1.5, 3.3, 0.025, 2.6);
    const [px, pz] = this.world(18.6, -25);
    this.box("cottage", "woodDark", px, terrainHeight(px, pz) + 0.28, pz, 1.9, 0.52, 0.7);
    for (let i = 0; i < 5; i++) {
      this.cylinder("cottage", "leafDark", px - 0.75 + i * 0.37, terrainHeight(px, pz) + 0.76, pz, 0.2, 0.4, 0.04, 6);
      this.cylinder("cottage", "lamp", px - 0.75 + i * 0.37, terrainHeight(px, pz) + 1, pz, 0.035, 0.045, 0.035, 6);
    }
  }

  private tower(): void {
    const { x, z } = this.site("lighthouse");
    for (const dx of [-6.13, 6.13]) for (const dz of [-6.13, 6.13]) {
      this.box("lighthouse", "plaster", x + dx, 17, z + dz, 0.45, 14, 0.45);
      for (let course = 0; course < 25; course++) {
        this.box("lighthouse", course % 5 === 0 ? "rock" : "plaster", x + dx, 10.3 + course * 0.55, z + dz, course % 2 ? 0.72 : 0.6, 0.5, course % 2 ? 0.6 : 0.72);
      }
      this.box("lighthouse", "rock", x + dx, 10.5, z + dz, 0.84, 1, 0.84);
      this.cylinder("lighthouse", "iron", x + dx * 0.88, 26.6, z + dz * 0.88, 0.09, 5, 0.09, 8);
    }
    for (const y of [12.95, 18.4, 23.95]) {
      for (const side of [-1, 1]) {
        this.box("lighthouse", "plaster", x + side * 6.3, y, z, 0.44, 0.27, 12.85);
        this.box("lighthouse", "plaster", x, y, z + side * 6.3, 12.85, 0.27, 0.44);
      }
    }
    for (const side of [-1, 1]) {
      for (const y of [14.95, 17.05]) this.box("lighthouse", "plaster", x + side * 6.24, y, z, 0.16, 0.15, 4.25);
      for (const dz of [-2.08, 2.08]) this.box("lighthouse", "plaster", x + side * 6.24, 16, z + dz, 0.16, 2.15, 0.15);
    }
    for (const y of [18.45, 20.55]) this.box("lighthouse", "plaster", x, y, z + 6.24, 4.25, 0.15, 0.16);
    for (const dx of [-2.08, 2.08]) this.box("lighthouse", "plaster", x + dx, 19.5, z + 6.24, 0.15, 2.15, 0.16);
    // The lantern house is open-sided: climbable platform has a full coastal view.
    this.cylinder("lighthouse", "roof", x, 29.4, z, 7.5, 1.65, 0.5, 8);
    this.cylinder("lighthouse", "iron", x, 28.58, z, 7.6, 0.12, 7.6, 8);
    this.cylinder("lighthouse", "brass", x, 30.35, z, 0.23, 0.7, 0.03, 8);
    this.cylinder("lighthouse", "iron", x, 25.2, z, 0.28, 2.35, 0.22, 12);
    this.cylinder("lighthouse", "brass", x, 26.4, z, 0.7, 0.18, 0.7, 16);
    this.plaque("lighthouse", "THE SAFE LIGHT", x + 1.8, 12.05, z - 6.28, 2.5);
    this.lantern("lighthouse", x - 4.5, 12.65, z - 6.3);
    this.lantern("lighthouse", x + 5.5, 25.2, z + 5.5);
    // Repeated thin stair nosings make every collision tread readable.
    for (const solid of LANTERN_SOLIDS) if (solid.site === "lighthouse" && solid.id.includes("stair") && !solid.id.includes("guard") && solid.collider.kind === "box") {
      const c = solid.collider;
      this.box("lighthouse", "woodLight", (c.minX + c.maxX) / 2, c.y1 + 0.008, (c.minZ + c.maxZ) / 2, c.maxX - c.minX - 0.04, 0.015, c.maxZ - c.minZ - 0.04);
    }
  }

  private wreck(): void {
    const { x, z } = this.site("wreck");
    // Exposed ribs hug the authored hull sides; the east entry and deck remain clear.
    for (let i = 0; i < 7; i++) {
      const zz = z - 5.35 + i * 1.65;
      this.beam("wreck", "woodDark", new Vector3(x - 2.45, 1.95, zz), new Vector3(x - 2.9, 4.65 - (i % 3) * 0.4, zz), 0.18);
      if (i < 2 || i > 4) this.beam("wreck", "woodDark", new Vector3(x + 2.45, 1.9, zz), new Vector3(x + 2.95, 3.8 - (i % 2) * 0.5, zz), 0.18);
    }
    for (const y of [1.95, 2.5, 3.1]) {
      this.box("wreck", "woodDark", x - 2.86, y, z, 0.12, 0.13, 12.3);
      for (const end of [-1, 1]) this.box("wreck", "wood", x + 2.68, y, z + end * 4.9, 0.13, 0.16, 2.2);
    }
    for (const zz of [z - 6.2, z + 6.2]) {
      this.beam("wreck", "woodDark", new Vector3(x - 2.5, 2.4, zz), new Vector3(x, 4.2, zz), 0.25);
      this.beam("wreck", "wood", new Vector3(x, 4.2, zz), new Vector3(x + 2.5, 2.4, zz), 0.22);
    }
    const mast = this.cylinder("wreck", "woodDark", x - 3.8, 2.35, z - 1.5, 0.22, 9.5, 0.13, 10);
    mast.rotation.z = 1.39; mast.rotation.y = 0.28;
    this.box("wreck", "sail", x - 4.8, 1.95, z - 1.6, 2.8, 0.07, 1.3, -0.12);
    this.cylinder("wreck", "rope", x + 3.2, 1.68, z + 4.2, 0.6, 0.1, 0.6, 16);
    this.plaque("wreck", "MERCY · 1748", x, 3.12, z + 6.22, 2.1, Math.PI);
    for (let i = 0; i < 6; i++) {
      const px = x + 4 + Math.sin(i * 2) * 2, pz = z + 6 + Math.cos(i * 2) * 2;
      this.box("wreck", "plank", px, this.surfaceHeight(px, pz) + 0.07, pz, 0.2, 0.1, 1.4, i);
    }
  }

  private overlook(): void {
    const { x, z } = this.site("overlook");
    this.rock("overlook", x - 3.2, 6.15, z - 1.9, 3.6, 2.5, 3.3);
    this.rock("overlook", x - 2.6, 7.05, z - 1.95, 2.5, 0.8, 2.3);
    this.plaque("overlook", "GULL ROCK", x + 3.3, 6.23, z + 1.1, 1.7);
    this.cylinder("overlook", "wood", x + 3.3, 5.55, z + 1.1, 0.08, 1.4);
    const marks = this.interaction("markings");
    // The carving is on the authored stone's exposed south face, behind its air target.
    const faceZ = z - 0.265;
    this.box("overlook", "brass", marks.x, marks.y, faceZ, 0.82, 0.035, 0.03);
    this.box("overlook", "brass", marks.x, marks.y, faceZ, 0.035, 0.42, 0.03);
    this.box("overlook", "lamp", marks.x - 0.45, marks.y, faceZ + 0.015, 0.15, 0.13, 0.05);
    this.plaque("overlook", "AMBER · WEST", marks.x, 6.68, z - 0.25, 2.2, Math.PI);
    for (let i = 0; i < 3; i++) {
      const gx = x - 3.5 + i * 0.65, gz = z - 2;
      this.box("overlook", "white", gx, 7.65, gz, 0.24, 0.13, 0.14);
      this.box("overlook", "iron", gx - 0.05, 7.66, gz, 0.34, 0.035, 0.1, 0.3);
      this.box("overlook", "gold", gx + 0.15, 7.67, gz, 0.09, 0.035, 0.045);
    }
  }

  private grotto(): void {
    // Rock dressing remains beyond the 6.5 m corridor and above its collidable roof.
    for (let i = 0; i < 9; i++) {
      const zz = -11 + i * 3;
      for (const xx of [-50.5, -42]) {
        const [x, z] = this.world(xx, zz);
        this.rock("grotto", x, 3.55, z, 2.2, 4.4, 3.5, i % 2 === 0);
      }
      const [x, z] = this.world(-46.4, zz);
      this.rock("grotto", x, 5.36, z, 6.6, 1.4, 3.5, i % 2 === 0);
      this.rock("grotto", LANTERN_ORIGIN.x - 49.62, 3.15, z, 0.35, 2.5, 1.8, true);
      this.rock("grotto", LANTERN_ORIGIN.x - 42.87, 3.3, z + 0.6, 0.35, 2.1, 1.6);
    }
    for (const zz of [-10, -2, 6, 12]) {
      const [x, z] = this.world(-49.1, zz);
      this.box("grotto", "woodDark", x, 2.15, z, 0.18, 1.1, 0.18);
      this.lantern("grotto", x, 2.8, z);
    }
    const [x, z] = this.world(-48.6, 8);
    this.box("grotto", "wood", x, 1.9, z, 0.85, 0.5, 1.4);
    this.box("grotto", "sail", x, 2.21, z, 0.8, 0.13, 1.32);
    this.cylinder("grotto", "brass", x + 0.35, 2.36, z + 0.4, 0.12, 0.25);
    this.plaque("grotto", "ALL SEVEN CAME ASHORE", x - 0.55, 3.42, z, 2.1, -Math.PI / 2);
    const [ex, ez] = this.world(-46.3, -13.7);
    this.plaque("grotto", "KEEPER'S PASSAGE", ex - 2.3, 3.05, ez, 2.2);
    this.cylinder("grotto", "wood", ex - 2.3, 2.35, ez, 0.08, 1.4);
  }

  private coastalClusters(): void {
    const clusters = [
      ["landing", 48, -48], ["landing", 26, -52], ["cottage", 19, -20], ["cottage", 5, -22],
      ["overlook", -27, -47], ["overlook", -13, -49], ["wreck", -67, 18], ["wreck", -52, 29],
      ["lighthouse", 20, 38], ["lighthouse", 39, 39], ["grotto", -53, -8], ["grotto", -39, 8],
    ] as const;
    for (const [site, lx, lz] of clusters) {
      const [x, z] = this.world(lx, lz);
      // Leave the entire shared trail width plus carrying room free of taller foliage.
      let clear = true;
      for (const path of LANTERN_PATHS) for (let i = 1; i < path.points.length; i++) {
        const a = path.points[i - 1]!, b = path.points[i]!, dx = b.x - a.x, dz = b.z - a.z;
        const length2 = dx * dx + dz * dz;
        if (length2 < 0.01) continue;
        const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / length2));
        if (Math.hypot(x - a.x - t * dx, z - a.z - t * dz) < path.width / 2 + 1.4) clear = false;
      }
      if (!clear) continue;
      const ground = this.surfaceHeight(x, z);
      this.rock(site, x, ground + 0.12, z, 1.2, 0.45, 0.8);
      for (let i = 0; i < 5; i++) {
        const angle = i * 2.4, px = x + Math.cos(angle) * 0.7, pz = z + Math.sin(angle) * 0.7;
        const shrub = CreateSphere(`lantern:${site}:saltbush`, { diameter: 1, segments: 4 }, this.scene);
        shrub.scaling.set(0.65, 0.48, 0.7); shrub.bakeCurrentTransformIntoVertices();
        sphereProjectUVs(shrub, SURFACE_PROFILES.leaf.metres);
        shrub.position.set(px, this.surfaceHeight(px, pz) + 0.22, pz); shrub.convertToFlatShadedMesh();
        this.add(shrub, site, i % 2 ? "leaf" : "leafDark", false);
      }
    }
  }

  private book(site: string, x: number, y: number, z: number): void {
    this.box(site, "leather", x, y - 0.13, z, 0.62, 0.045, 0.42);
    this.box(site, "paper", x - 0.145, y - 0.09, z, 0.275, 0.035, 0.39);
    this.box(site, "paper", x + 0.145, y - 0.09, z, 0.275, 0.035, 0.39);
    for (let i = 0; i < 5; i++) for (const side of [-1, 1]) this.box(site, "woodDark", x + side * 0.15, y - 0.068, z - 0.13 + i * 0.055, 0.19 - (i % 2) * 0.04, 0.003, 0.009);
  }

  private storyObjects(): void {
    for (const id of ["journal", "logbook"] as const) {
      const item = this.interaction(id);
      const solid = LANTERN_SOLIDS.find(s => s.id === (id === "journal" ? "journal-table" : "logbook-desk"))!;
      const c = solid.collider;
      if (c.kind !== "box") continue;
      const x = id === "journal" ? c.minX + 0.3 : c.maxX - 0.18;
      this.book(item.site, x, c.y1 + 0.16, item.z);
      this.lantern(item.site, (c.minX + c.maxX) / 2, c.y1 + 0.3, c.maxZ - 0.26);
    }
    for (const id of ["bearing", "colour", "signal"] as const) {
      const item = this.interaction(id);
      this.box("lighthouse", "woodDark", item.x, item.y - 0.42, item.z, 0.62, 0.55, 0.5);
      const dial = this.cylinder("lighthouse", id === "colour" ? "brass" : "iron", item.x, item.y - 0.07, item.z, 0.23, 0.1, 0.23, 16);
      dial.rotation.x = 0.25;
      this.box("lighthouse", "brass", item.x, item.y + 0.015, item.z, 0.05, 0.04, 0.29);
    }
    const cache = this.interaction("cache");
    const ground = LANTERN_REWARDS.cache.y;
    for (const dx of [-0.6, 0.6]) this.box(cache.site, "wood", cache.x + dx, ground + 0.3, cache.z, 0.08, 0.6, 0.95);
    for (const dz of [-0.45, 0.45]) this.box(cache.site, "wood", cache.x, ground + 0.3, cache.z + dz, 1.2, 0.6, 0.08);
    for (const dx of [-0.35, 0.35]) this.box(cache.site, "iron", cache.x + dx, ground + 0.3, cache.z - 0.495, 0.045, 0.6, 0.025);
    const supplies = this.interaction("supplies");
    this.box(supplies.site, "plank", supplies.x, supplies.y - 0.8, supplies.z, 0.9, 0.8, 0.8);
    const lid = this.box(supplies.site, "woodDark", supplies.x, supplies.y - 0.28, supplies.z + 0.45, 0.94, 0.08, 0.85);
    lid.rotation.x = -0.6;
    this.box(supplies.site, "paintRed", supplies.x, supplies.y - 0.47, supplies.z - 0.412, 0.13, 0.4, 0.015);
    this.box(supplies.site, "paintRed", supplies.x, supplies.y - 0.47, supplies.z - 0.412, 0.4, 0.13, 0.015);
  }
}
